// src/App.js
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import PageWrapper from './components/PageWrapper';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import SolutionsPage from './pages/SolutionsPage';
import PricingPage from './pages/PricingPage';
import DemoPage from './pages/DemoPage';
import ResourcesPage from './pages/ResourcesPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import ProductOnePager from './pages/ProductOnePager';
import WCAGChecklist from './pages/WCAGChecklist';
import SecurityBrief from './pages/SecurityBrief';
import TranslateViewerPage from './pages/TranslateViewerPage';
import TranslateHubPage from './pages/TranslateHubPage';
import UtilityAllowanceHubPage from './pages/UtilityAllowanceHubPage';
import UtilityAllowanceViewerPage from './pages/UtilityAllowanceViewerPage';
import PublicHousingUAPage from './pages/PublicHousingUAPage';
import PublicHousingIntakePage from './pages/PublicHousingIntakePage';
import LihtcIntakePage from './pages/LihtcIntakePage';
import AuthorityUAOverviewPage from './pages/AuthorityUAOverviewPage';
import UtilityAllowanceJoinPage from './pages/UtilityAllowanceJoinPage';
import UtilityAllowanceJoinPaymentPage from './pages/UtilityAllowanceJoinPaymentPage';
import CalculatorPage from './pages/CalculatorPage';
import SarasotaCalculatorPage from './pages/SarasotaCalculatorPage';
import QrPage from './pages/QrPage';
import HousingNavigatorChatPage from './pages/HousingNavigatorChatPage';
import ChampChatPage from './pages/ChampChatPage';
import QAResultsPage from './pages/QAResultsPage';
import RecertRedirectPage from './pages/RecertRedirectPage';
import HcvShortfallPage from './pages/HcvShortfallPage';
import HcvShortfallAuthorityPage from './pages/HcvShortfallAuthorityPage';
import usePageTracking from './hooks/usePageTracking';

const AppContent = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Analytics: track every SPA route change + UTM capture on first load
  usePageTracking();
  
  // Hide nav and footer on the onepager route
  const hideNavAndFooter = ['/onepager', '/wcag-checklist', '/security-brief', '/translate', '/qr', '/utility-allowances', '/qa-results', '/housingnavigator', '/champ'].includes(location.pathname) || location.pathname.startsWith('/translate/') || location.pathname.startsWith('/utility-allowances/') || location.pathname.startsWith('/hcv-shortfall');
  // The resident tools and chat pages render their own <main>; the standalone
  // documents (one-pager, checklist, security brief, QR card) do not.
  const pageHasOwnMain = ['/translate', '/utility-allowances', '/qa-results', '/housingnavigator', '/champ'].includes(location.pathname)
    || location.pathname.startsWith('/translate/') || location.pathname.startsWith('/utility-allowances/') || location.pathname.startsWith('/hcv-shortfall');
  const Main = pageHasOwnMain ? 'div' : 'main';

  return (
    <div className="min-h-screen bg-white">
      {/* First Tab stop on every page: lets keyboard and screen-reader users jump past
          the navigation (WCAG 2.4.1). Visually hidden until focused. */}
      <a href="#main" className="skip-link">Skip to main content</a>
      {!hideNavAndFooter && <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />}

      {/* The main landmark and the skip link's target. The resident tools (the routes
          without the site nav) render their own <main>, so for them this is a plain
          div and every route still has exactly one main landmark. */}
      <Main id="main" tabIndex={-1} className="swipe-container">
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
            <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
            <Route path="/product" element={<PageWrapper><ProductPage /></PageWrapper>} />
            <Route path="/solutions" element={<PageWrapper><SolutionsPage /></PageWrapper>} />
            <Route path="/pricing" element={<PageWrapper><PricingPage /></PageWrapper>} />
            <Route path="/demo" element={<PageWrapper><DemoPage /></PageWrapper>} />
            <Route path="/resources" element={<PageWrapper><ResourcesPage /></PageWrapper>} />
            <Route path="/contact" element={<PageWrapper><ContactPage /></PageWrapper>} />
            <Route path="/login" element={<PageWrapper><LoginPage /></PageWrapper>} />
            <Route path="/dashboard" element={<PageWrapper><DashboardPage /></PageWrapper>} />
            <Route path="/qa-results" element={<QAResultsPage />} />
            <Route path="/privacy" element={<PageWrapper><PrivacyPage /></PageWrapper>} />
            <Route path="/terms" element={<PageWrapper><TermsPage /></PageWrapper>} />
            <Route path="/onepager" element={<ProductOnePager />} />
            <Route path="/wcag-checklist" element={<WCAGChecklist />} />
            <Route path="/security-brief" element={<SecurityBrief />} />
            <Route path="/translate" element={<TranslateHubPage />} />
            <Route path="/calculator" element={<PageWrapper><CalculatorPage /></PageWrapper>} />
            {/* Temporary Sarasota HA mockup with utility allowances — intentionally unlinked */}
            <Route path="/calculator/sarasota" element={<PageWrapper><SarasotaCalculatorPage /></PageWrapper>} />
            <Route path="/translate/:org/:lang" element={<TranslateViewerPage />} />
            <Route path="/utility-allowances" element={<UtilityAllowanceHubPage />} />
            <Route path="/utility-allowances/join" element={<UtilityAllowanceJoinPage />} />
            <Route path="/utility-allowances/join/payment" element={<UtilityAllowanceJoinPaymentPage />} />
            {/* Public housing (24 CFR 965) per-development scaffold — intentionally unlinked */}
            <Route path="/utility-allowances/public-housing" element={<PublicHousingIntakePage />} />
            {/* Unlinked LIHTC intake — 26 CFR 1.42-10, separate engine from public housing. */}
            <Route path="/utility-allowances/lihtc" element={<LihtcIntakePage />} />
            <Route path="/utility-allowances/:org/overview" element={<AuthorityUAOverviewPage />} />
            <Route path="/utility-allowances/:org/sample" element={<UtilityAllowanceViewerPage sample />} />
            <Route path="/utility-allowances/public-housing/:property" element={<PublicHousingUAPage />} />
            <Route path="/utility-allowances/:org" element={<UtilityAllowanceViewerPage />} />
            <Route path="/qr" element={<QrPage />} />
            <Route path="/housingnavigator" element={<HousingNavigatorChatPage />} />
            <Route path="/champ" element={<ChampChatPage />} />
            <Route path="/recert" element={<RecertRedirectPage />} />
            {/* HCV shortfall tracker — public, intentionally unlinked. Data from the compliance service's /api/hcv. */}
            <Route path="/hcv-shortfall" element={<HcvShortfallPage />} />
            <Route path="/hcv-shortfall/:slug" element={<HcvShortfallAuthorityPage />} />
          </Routes>
        </AnimatePresence>
      </Main>

      {!hideNavAndFooter && <Footer />}
    </div>
  );
};

const App = () => {
  return (
    <Router>
      {/* Honour the visitor's reduced-motion setting for every framer-motion animation,
          including the sliding page transitions (WCAG 2.3.3 / 2.2.2). */}
      <MotionConfig reducedMotion="user">
        <AppContent />
      </MotionConfig>
    </Router>
  );
};

export default App;